import React, { useCallback, useEffect } from "react";
import { ReleasePageWrapper } from "../ReleasePageWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WizardService } from "../../../services/WizardService";
import {
  Button,
  Col,
  Row,
  Space,
  TablePaginationConfig,
  message,
  Drawer,
  Popconfirm,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Adapter } from "../../../types/adapter";
import { TableQueryFilter } from "../../components/TableQueryFilter";
import { AdapterList, getAdapterColumns } from "./AdapterList";
import { AdapterForm } from "./AdapterForm";
import { TableColumnSelector } from "../../components/TableColumnSelector";

export const AdapterListPage = () => {
  const api = WizardService.Adapter;
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [records, setRecords] = React.useState<Adapter[]>([]);
  const [selectedRecords, setSelectedRecords] = React.useState<number[]>([]);
  const [editing, setEditing] = React.useState<number | undefined>(undefined);
  const [visibleColumns, setVisibleColumns] = React.useState<string[]>([]);

  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: searchParams.get("page") !== null ? +searchParams.get("page")! : 1,
    pageSize: searchParams.get("per") !== null ? +searchParams.get("per")! : 10,
    showSizeChanger: true,
  });

  // Handlers
  const viewRecord = (id: number) => {
    navigate(id.toString());
  };

  const editRecord = (id: number) => {
    setEditing(id);
  };

  const copyRecord = (id: number) => {
    setIsLoading(true);
    api
      .copy(id)
      .then((copiedAdapter) => {
        return api.all(searchParams).then((response) => {
          setRecords(response.items || []);
          setPagination((state) => ({
            ...state,
            current: response.metadata.page,
            pageSize: response.metadata.per,
            total: response.metadata.total,
          }));
          setEditing(copiedAdapter.id);
        });
      })
      .catch((error) => {
        message.error(error.message);
        const originalRecord = records.find((r) => r.id === id);
        if (originalRecord) {
          const copiedRecord: Adapter = {
            ...originalRecord,
            id: undefined,
            // Modify the status to indicate it's a copy
            status: `${originalRecord.status} (Copy)`,
          };
          setRecords([...records, copiedRecord]);
          setEditing(undefined);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const saveRecord = (record: Adapter) => {
    setIsLoading(true);
    let promise: Promise<any>;
    if (record.id !== undefined && record.id > 0) {
      promise = api.update(record.id, record);
    } else {
      promise = api.create(record);
    }

    promise
      .then((_) => fetch())
      .then(() => {
        setEditing(undefined);
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const removeRecord = (id: number) => {
    setIsLoading(true);
    api
      .delete(id)
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const removeSelectedRecords = () => {
    setIsLoading(true);
    Promise.all(selectedRecords.map((id) => api.delete(id)))
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  // Callbacks
  const fetch = useCallback(() => {
    setSelectedRecords([]);
    setIsLoading(true);
    api
      .all(searchParams)
      .then((response) => {
        setRecords(response.items || []);
        setPagination((state) => ({
          ...state,
          current: response.metadata.page,
          pageSize: response.metadata.per,
          total: response.metadata.total,
        }));
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  }, [searchParams, api]);

  const updateSearchQuery = useCallback(() => {
    const params = searchParams;

    pagination.current
      ? params.set("page", pagination.current.toString())
      : params.delete("page");
    pagination.pageSize
      ? params.set("per", pagination.pageSize.toString())
      : params.delete("per");

    setSearchParams(params);
  }, [pagination, setSearchParams, searchParams]);

  // Hooks
  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    updateSearchQuery();
  }, [updateSearchQuery]);

  return (
    <ReleasePageWrapper>
      <Row
        style={{
          marginTop: "10px",
          marginBottom: "10px",
          paddingBottom: "8px",
        }}
      >
        <Col flex="auto">
          <TableQueryFilter filter={["platform", "pattern", "version"]} />
        </Col>
        <Col flex="right">
          <Space>
            <TableColumnSelector
              columns={getAdapterColumns(true)}
              onChange={setVisibleColumns}
              storageKey="adapter-table-columns"
            />
            <Button
              type="primary"
              icon={<PlusOutlined  />}
              onClick={(_) => setEditing(0)}
            >
              Create
            </Button>
            <Popconfirm
              title="Delete Adapter"
              description="Are you sure to delete selected adapters?"
              onConfirm={removeSelectedRecords}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<DeleteOutlined  />}
                disabled={selectedRecords.length === 0}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>
      <Row>
        <Col flex="100%">
          <AdapterList
            loading={isLoading}
            records={records}
            pagination={pagination}
            onChange={(pagination) => setPagination(pagination)}
            viewRecord={viewRecord}
            editRecord={editRecord}
            copyRecord={copyRecord}
            removeRecord={removeRecord}
            selectRecods={setSelectedRecords}
            visibleColumns={visibleColumns}
            editable
          />
        </Col>
      </Row>
      <Drawer
        title={
          editing !== undefined && editing > 0
            ? "Edit Adapter Release #" + editing
            : "Create a new Adapter Release"
        }
        width={600}
        onClose={(_) => setEditing(undefined)}
        open={editing !== undefined}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
      >
        <AdapterForm
          adapter={records.filter((r) => r.id === editing)[0]}
          isLoading={isLoading}
          onFinish={saveRecord}
        />
      </Drawer>
    </ReleasePageWrapper>
  );
};
